import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";

// Fetch prompts by category
export const fetchPromptsByCategory = createAsyncThunk(
  "promptMaster/fetchByCategory",
  async (category, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("prompt_master")
        .select("*")
        .eq("category", category)
        .order("index", { ascending: true });

      if (error) throw error;
      return { category, prompts: data || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Save prompts for a category
export const savePromptsForCategory = createAsyncThunk(
  "promptMaster/saveForCategory",
  async ({ category, prompts }, { rejectWithValue }) => {
    try {
      // First, delete existing prompts for this category
      const { error: deleteError } = await supabase
        .from("prompt_master")
        .delete()
        .eq("category", category);

      if (deleteError) throw deleteError;

      // Then insert new prompts with proper indexing
      const promptsToInsert = prompts
        .filter(prompt => prompt.label && prompt.prompt) // Only save prompts with content
        .map((prompt, index) => ({
          label: prompt.label,
          prompt: prompt.prompt,
          category: category,
          index: index,
          status: prompt.status !== false, // Default to true if not specified
          updated_at: new Date().toISOString()
        }));

      if (promptsToInsert.length === 0) {
        return { category, prompts: [] };
      }

      const { data, error } = await supabase
        .from("prompt_master")
        .insert(promptsToInsert)
        .select();

      if (error) throw error;
      return { category, prompts: data || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update prompt status (activate/deactivate)
export const updatePromptStatus = createAsyncThunk(
  "promptMaster/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("prompt_master")
        .update({ 
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete a prompt
export const deletePrompt = createAsyncThunk(
  "promptMaster/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from("prompt_master")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update prompt order
export const updatePromptOrder = createAsyncThunk(
  "promptMaster/updateOrder",
  async ({ category, prompts }, { rejectWithValue }) => {
    try {
      const updates = prompts
        .filter(prompt => prompt.dbId) // Only update prompts that have database IDs
        .map((prompt, index) => ({
          id: prompt.dbId, // Use the database ID
          index: index,
          updated_at: new Date().toISOString()
        }));

      if (updates.length === 0) {
        return { category, prompts };
      }

      const { error } = await supabase
        .from("prompt_master")
        .upsert(updates, { onConflict: "id" });

      if (error) throw error;
      return { category, prompts };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const promptMasterSlice = createSlice({
  name: "promptMaster",
  initialState: {
    promptsByCategory: {
      industryTrends: [],
      workforceImpact: [],
      training: [],
      strategicSummary: [],
    },
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateLocalPrompts: (state, action) => {
      const { category, prompts } = action.payload;
      state.promptsByCategory[category] = prompts;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch prompts by category
      .addCase(fetchPromptsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromptsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        const { category, prompts } = action.payload;
        state.promptsByCategory[category] = prompts;
      })
      .addCase(fetchPromptsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save prompts for category
      .addCase(savePromptsForCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(savePromptsForCategory.fulfilled, (state, action) => {
        state.saving = false;
        const { category, prompts } = action.payload;
        state.promptsByCategory[category] = prompts;
      })
      .addCase(savePromptsForCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      // Update prompt status
      .addCase(updatePromptStatus.fulfilled, (state, action) => {
        const updatedPrompt = action.payload;
        Object.keys(state.promptsByCategory).forEach(category => {
          const promptIndex = state.promptsByCategory[category].findIndex(
            p => p.id === updatedPrompt.id
          );
          if (promptIndex !== -1) {
            state.promptsByCategory[category][promptIndex] = updatedPrompt;
          }
        });
      })
      .addCase(updatePromptStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete prompt
      .addCase(deletePrompt.fulfilled, (state, action) => {
        const deletedId = action.payload;
        Object.keys(state.promptsByCategory).forEach(category => {
          state.promptsByCategory[category] = state.promptsByCategory[category].filter(
            p => p.id !== deletedId
          );
        });
      })
      .addCase(deletePrompt.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update prompt order
      .addCase(updatePromptOrder.fulfilled, (state, action) => {
        const { category, prompts } = action.payload;
        state.promptsByCategory[category] = prompts;
      })
      .addCase(updatePromptOrder.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, updateLocalPrompts } = promptMasterSlice.actions;
export default promptMasterSlice.reducer; 